import React, { Dispatch, SetStateAction, use, useMemo, useState } from 'react';
import { isDeepEqual } from '@fabric-ui/utils';
import { useNavigationBlocker } from '@hooks/useNavigationBlocker';
import { EntityProjectionName, entityProjectionNames } from '@utils/entityProjectionNames';
import { ISyncStatus, SettingType, useIsSettingSyncStatusBySettingsType } from '@views/Settings/hooks';
import { EntityBuildingBlock, EntityWeight, GetRiskScoringConfigResponse, ProjectionToBaseScore } from './types';
import { transformRiskScoringConfig } from './utils';

export interface RiskScoreContextType {
  entityWeights: EntityWeight[];
  setEntityWeights: Dispatch<SetStateAction<EntityWeight[]>>;
  buildingBlocks: EntityBuildingBlock[];
  setBuildingBlocks: Dispatch<SetStateAction<EntityBuildingBlock[]>>;
  editableBaseScores: ProjectionToBaseScore[];
  setEditableBaseScores: Dispatch<SetStateAction<ProjectionToBaseScore[]>>;
  initialWeights: EntityWeight[];
  initialBuildingBlocks: EntityBuildingBlock[];
  initialBaseScores: ProjectionToBaseScore[];
  isDirty: boolean;
  riskScoreProjectionName: string;
  data: GetRiskScoringConfigResponse;
  syncStatus: {
    allowOverride: boolean;
    setAllowOverrideSPF: Dispatch<SetStateAction<boolean>>;
    syncInfo: ISyncStatus;
    isSyncStatusLoading: boolean;
  };
}

const RiskScoreContext = React.createContext<RiskScoreContextType | null>(null);

export interface RiskScoreProviderProps {
  children: React.ReactNode;
  data: GetRiskScoringConfigResponse;
  riskScoreProjectionName: string;
}

const projectionToSettingType: Partial<Record<EntityProjectionName, SettingType>> = {
  [entityProjectionNames.Asset]: SettingType.ASSET_RISK_SCORE,
};

export const RiskScoreProvider = ({ children, data, riskScoreProjectionName }: RiskScoreProviderProps) => {
  const { entityWeights: initialWeights, buildingBlocks: initialBuildingBlocks } = useMemo(
    () => transformRiskScoringConfig(data, riskScoreProjectionName),
    [data, riskScoreProjectionName]
  );
  const initialBaseScores = data.projectionToBaseScores;

  const [entityWeights, setEntityWeights] = useState<EntityWeight[]>(initialWeights);
  const [buildingBlocks, setBuildingBlocks] = useState<EntityBuildingBlock[]>(initialBuildingBlocks);
  const [editableBaseScores, setEditableBaseScores] = useState<ProjectionToBaseScore[]>(initialBaseScores);
  const [allowOverrideSPF, setAllowOverrideSPF] = useState(false);

  const settingType = projectionToSettingType[riskScoreProjectionName] ?? SettingType.ASSET_RISK_SCORE;
  const {
    data: syncInfo = { synced: false, configuredThrough: settingType, projectionToAffectedFields: [] },
    isLoading: isSyncStatusLoading,
  } = useIsSettingSyncStatusBySettingsType(settingType);
  const allowOverride = syncInfo.synced || allowOverrideSPF || isSyncStatusLoading;

  const isDirty = useMemo(
    () =>
      !isDeepEqual(entityWeights, initialWeights) ||
      !isDeepEqual(buildingBlocks, initialBuildingBlocks) ||
      !isDeepEqual(editableBaseScores, initialBaseScores),
    [entityWeights, initialWeights, buildingBlocks, initialBuildingBlocks, editableBaseScores, initialBaseScores]
  );

  useNavigationBlocker({ isDirty });

  const value = useMemo(
    () => ({
      entityWeights,
      setEntityWeights,
      buildingBlocks,
      setBuildingBlocks,
      editableBaseScores,
      setEditableBaseScores,
      initialWeights,
      initialBuildingBlocks,
      initialBaseScores,
      isDirty,
      riskScoreProjectionName,
      data,
      syncStatus: {
        allowOverride,
        setAllowOverrideSPF,
        syncInfo,
        isSyncStatusLoading,
      },
    }),
    [
      entityWeights,
      buildingBlocks,
      editableBaseScores,
      initialWeights,
      initialBuildingBlocks,
      initialBaseScores,
      isDirty,
      riskScoreProjectionName,
      data,
      allowOverride,
      setAllowOverrideSPF,
      syncInfo,
      isSyncStatusLoading,
    ]
  );

  return <RiskScoreContext value={value}>{children}</RiskScoreContext>;
};

export const useRiskScoreContext = (): RiskScoreContextType => {
  const ctx = use(RiskScoreContext);
  if (!ctx) {
    throw new Error('useRiskScoreContext must be used within a RiskScoreProvider');
  }
  return ctx;
};
